import { useEffect, useState } from 'react';
import { 
  FaUser, 
  FaSearch, 
  FaEye, 
  FaTrash, 
  FaCalendarAlt,
  FaGlobe,
  FaEnvelope,
  FaExclamationTriangle,
  FaTimes,
  FaUserPlus,
  FaFilter
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Pagination from './Pagination';
import Nav from './nav';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  });
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    newToday: 0,
    active: 0
  });

  // Open delete confirmation modal
  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedUser(null);
  };

  // Confirm and execute user deletion
  const confirmDelete = async () => {
    if (!selectedUser) return;
    
    try {
      const adminToken = localStorage.getItem('adminToken');
      const adminEmail = localStorage.getItem('adminEmail');
      
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/admin/users/${selectedUser._id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'email': adminEmail,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to delete user');
      
      toast.success('User deleted successfully');
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.message);
    } finally {
      closeDeleteModal();
    }
  };

  // Fetch users with pagination and search
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const adminToken = localStorage.getItem('adminToken');
      const adminEmail = localStorage.getItem('adminEmail');

      // Build query parameters
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        ...(search && { search })
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/admin/users?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'email': adminEmail,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to load users');

      const data = await response.json();
      setUsers(data.users);
      setPagination(prev => ({
        ...prev,
        total: data.total,
        pages: data.pages,
      }));

      // Update stats
      setStats({
        total: data.total,
        newToday: 0, // You might want to calculate this from your data
        active: data.users.length
      });

    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, pagination.limit, search]);

  const updateSearch = (value) => {
    setSearch(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400 text-lg">Loading users...</p>
          <p className="text-gray-500 text-sm mt-2">Please wait while we fetch the data</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 max-w-md text-center backdrop-blur-sm">
          <FaExclamationTriangle className="text-red-400 text-4xl mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Error Loading Users</h2>
          <p className="text-red-400 mb-6">{error}</p>
          <div className="space-y-3">
            <button
              onClick={fetchUsers}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors duration-200 font-medium"
            >
              Try Again
            </button>
            <Link 
              to="/admin/login" 
              className="block text-blue-400 hover:text-blue-300 text-sm transition-colors duration-200"
            >
              Or login again
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Header Section */}
      <div className="border-b border-gray-700/50 bg-gray-900/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-6 space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                User Management
              </h1>
              <p className="text-gray-400 mt-1">Manage and monitor all user accounts</p>
            </div>
            
            {/* Search Bar */}
            <div className="relative w-full sm:w-80">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={search}
                  onChange={(e) => updateSearch(e.target.value)}
                  className="w-full bg-gray-800/50 border border-gray-600 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm transition-all duration-200"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 rounded-2xl p-6 border border-blue-500/20 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-400 text-sm font-medium mb-2">Total Users</p>
                <p className="text-3xl font-bold text-white">{stats.total}</p>
              </div>
              <div className="p-3 bg-blue-500/20 rounded-xl">
                <FaUser className="text-blue-400 text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 rounded-2xl p-6 border border-green-500/20 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-400 text-sm font-medium mb-2">Active Today</p>
                <p className="text-3xl font-bold text-white">{stats.active}</p>
              </div>
              <div className="p-3 bg-green-500/20 rounded-xl">
                <FaUserPlus className="text-green-400 text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 rounded-2xl p-6 border border-purple-500/20 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-400 text-sm font-medium mb-2">New Today</p>
                <p className="text-3xl font-bold text-white">{stats.newToday}</p>
              </div>
              <div className="p-3 bg-purple-500/20 rounded-xl">
                <FaFilter className="text-purple-400 text-xl" />
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-gray-800/30 backdrop-blur-sm rounded-2xl border border-gray-700/50 overflow-hidden">
          {/* Table Header */}
          <div className="px-6 py-4 border-b border-gray-700/50">
            <h2 className="text-lg font-semibold text-white">All Users</h2>
            <p className="text-gray-400 text-sm">Showing {users.length} of {pagination.total} users</p>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700/50">
                  <th className="text-left py-4 px-6 text-gray-400 font-medium text-sm uppercase tracking-wider">
                    User
                  </th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium text-sm uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium text-sm uppercase tracking-wider">
                    Location
                  </th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium text-sm uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="text-right py-4 px-6 text-gray-400 font-medium text-sm uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {users.map((user) => (
                  <tr 
                    key={user._id} 
                    className="hover:bg-gray-700/20 transition-colors duration-150 group"
                  >
                    {/* User Info */}
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-4">
                        <div className="relative">
                          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center text-white font-bold text-lg">
                            {user.name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-gray-800 rounded-full"></div>
                        </div>
                        <div>
                          <div className="font-semibold text-white group-hover:text-blue-300 transition-colors">
                            {user.name}
                          </div>
                          <div className="text-gray-400 text-sm">ID: {user._id.slice(-8)}</div>
                        </div>
                      </div>
                    </td>

                    {/* Contact Info */}
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2 text-gray-300">
                        <FaEnvelope className="text-gray-400" />
                        <span className="text-sm">{user.email}</span>
                      </div>
                      {user.phone && (
                        <div className="flex items-center space-x-2 text-gray-400 text-sm mt-1">
                          <span>{user.phone}</span>
                        </div>
                      )}
                    </td>

                    {/* Location */}
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        <FaGlobe className="text-gray-400" />
                        <span className="text-gray-300">{user.country || 'Not specified'}</span>
                      </div>
                      <div className="text-gray-400 text-sm mt-1">
                        {user.currency || 'USD'}
                      </div>
                    </td>

                    {/* Join Date */}
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2 text-gray-300">
                        <FaCalendarAlt className="text-gray-400" />
                        <span>{new Date(user.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="text-gray-400 text-sm mt-1">
                        {new Date(user.createdAt).toLocaleTimeString()}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="py-4 px-6 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/admin/users/${user._id}`}
                          className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-all duration-200 font-medium group/btn"
                        >
                          <FaEye className="text-sm" />
                          <span>View</span>
                        </Link>
                        <button
                          onClick={() => openDeleteModal(user)}
                          className="inline-flex items-center space-x-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 hover:text-red-300 border border-red-500/30 hover:border-red-500/50 py-2 px-4 rounded-lg transition-all duration-200 font-medium group/btn"
                        >
                          <FaTrash className="text-sm" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {users.length === 0 && (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-gray-700/50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FaUser className="text-gray-400 text-2xl" />
              </div>
              <h3 className="text-lg font-semibold text-gray-300 mb-2">No users found</h3>
              <p className="text-gray-400 max-w-sm mx-auto">
                {search ? 'Try adjusting your search terms' : 'No users have been registered yet'}
              </p>
            </div>
          )}

          {/* Pagination */}
          {users.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-700/50">
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.pages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>

      {/* Bottom Navigation */}
    <div style={{marginTop: '15%'}}>
        <Nav />

    </div>
      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl border border-gray-700/50 p-6 max-w-md w-full backdrop-blur-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Confirm Deletion</h3>
              <button
                onClick={closeDeleteModal}
                className="text-gray-400 hover:text-gray-300 transition-colors duration-200"
              >
                <FaTimes className="text-lg" />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center space-x-3 mb-4 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                <FaExclamationTriangle className="text-red-400 text-xl" />
                <p className="text-red-400 text-sm font-medium">
                  This action cannot be undone!
                </p>
              </div>
              
              <p className="text-gray-300">
                Are you sure you want to permanently delete user:
              </p>
              <div className="mt-2 p-3 bg-gray-700/30 rounded-lg border border-gray-600/30">
                <div className="font-semibold text-white text-lg">{selectedUser?.name}</div>
                <div className="text-gray-400 text-sm">{selectedUser?.email}</div>
              </div>
              <p className="text-gray-400 text-sm mt-3">
                All user data including wallet, deposits, and withdrawals will be permanently removed.
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeDeleteModal}
                className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-all duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-6 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl transition-all duration-200 font-medium shadow-lg shadow-red-500/25"
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;